import React, { Component, ErrorInfo, ReactElement, ReactNode } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { getErrorDetails } from "./error-boundary.utils";

interface ErrorBoundaryProps {
  children: ReactNode;
  FallbackComponent: ReactElement;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack?: string;
  datadogSessionId?: string;
  datadogApplicationId?: string;
  errorDetails?: string;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const context = datadogRum.getInternalContext();
    const sessionId = context?.session_id;
    const applicationId = context?.application_id;
    const errorDetails = getErrorDetails(error, errorInfo, "ErrorBoundary");

    datadogRum.addError(error, {
      errorInfo,
      context: {
        triggeredBy: "ErrorBoundary",
        sessionId,
        errorDetails
      }
    });

    datadogRum.addAction("error_boundary_crash", {
      errorName: error.name,
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
      sessionId,
      errorDetails
    });

    this.setState({
      componentStack: errorInfo.componentStack,
      datadogSessionId: sessionId,
      datadogApplicationId: applicationId,
      errorDetails
    });
  }

  render(): ReactNode {
    const { hasError, datadogSessionId, errorDetails, error } = this.state;

    if (hasError) {
      return React.cloneElement(this.props.FallbackComponent, {
        datadogSessionId,
        errorDetails,
        error
      });
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
